import Nullable from '@/types/nullable';
import { InvestmentAssetType, RiskProfileRule, RiskProfileRuleData, RiskProfileRuleType } from '@/types/risk-profiles';
import { formatMessage } from '@/utils/i18n';
import { getTargetValueFromMinMax, DEFAULT_RISK_COLORS, sortAssetRule } from '@/utils/risk-profiles';

const formatRiskProfileDataForStackBar = (rule: RiskProfileRule, ruleData: RiskProfileRuleData[]) => {
  const averages = ruleData.map(data => getTargetValueFromMinMax(data.target.min, data.target.max));
  const total = averages.reduce((sum, avg) => sum + avg, 0);
  const assetAllocationRule = rule.ruleType === RiskProfileRuleType.AssetAllocation;

  if (total === 0 && assetAllocationRule) {
    console.warn('RiskProfile Data Sum of averages is 0 for Asset Allocation rule');
  }

  const safeTotal = total || 1;

  // for value, we want to use normalized percentage for Asset Allocation rules. Otherwise, use the raw average value for non-Asset Allocation rules
  const slices = ruleData.map((data, index) => ({
    value: assetAllocationRule ? (averages[index] / safeTotal) * 100 : averages[index],
    color: DEFAULT_RISK_COLORS[rule.ruleType][data.key],
  }));

  return {
    slices,
    legend: getChartLegends(rule),
  };
};

/**
 * For the Risk profile card
 */
const getChartLegends = (rule: RiskProfileRule): Nullable<{ color: string; label: string }[]> => {
  switch (rule.ruleType) {
    case RiskProfileRuleType.AssetAllocation: {
      const legendsMap = {
        [InvestmentAssetType.Cash]: {
          id: InvestmentAssetType.Cash,
          color: DEFAULT_RISK_COLORS[rule.ruleType].CASH,
          label: formatMessage('RiskProfile.Rules.AssetType.CASH'),
        },
        [InvestmentAssetType.FixedIncome]: {
          id: InvestmentAssetType.FixedIncome,
          color: DEFAULT_RISK_COLORS[rule.ruleType].FIXED_INCOME,
          label: formatMessage('RiskProfile.Rules.AssetType.FIXED_INCOME'),
        },
        [InvestmentAssetType.Equity]: {
          id: InvestmentAssetType.Equity,
          color: DEFAULT_RISK_COLORS[rule.ruleType].EQUITY,
          label: formatMessage('RiskProfile.Rules.AssetType.EQUITY'),
        },
        [InvestmentAssetType.RealAssets]: {
          id: InvestmentAssetType.RealAssets,
          color: DEFAULT_RISK_COLORS[rule.ruleType].REAL_ASSETS,
          label: formatMessage('RiskProfile.Rules.AssetType.REAL_ASSETS'),
        },
        [InvestmentAssetType.AlternativeInvestment]: {
          id: InvestmentAssetType.AlternativeInvestment,
          color: DEFAULT_RISK_COLORS[rule.ruleType].ALTERNATIVE_INVESTMENT,
          label: formatMessage('RiskProfile.Rules.AssetType.ALTERNATIVE_INVESTMENT'),
        },
        [InvestmentAssetType.Other]: {
          id: InvestmentAssetType.Other,
          color: DEFAULT_RISK_COLORS[rule.ruleType].OTHER,
          label: formatMessage('RiskProfile.Rules.AssetType.OTHER'),
        },
        RISK_OTHER: {
          id: 'RISK_OTHER',
          color: DEFAULT_RISK_COLORS[rule.ruleType].RISK_OTHER,
          label: formatMessage('RiskProfile.Rules.AssetType.OTHER'),
        },
      };

      const sortedRule = sortAssetRule(rule);
      return sortedRule.value.flatMap(v => legendsMap[v.assetType]).filter(v => !!v);
    }
  }
  return null;
};

export default formatRiskProfileDataForStackBar;
